import React, { useState, useEffect, useRef } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Alert,
  Linking,
} from 'react-native';
import { CameraView, CameraType, useCameraPermissions, useMicrophonePermissions } from 'expo-camera';
import { useRouter, useLocalSearchParams, useFocusEffect } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { X, RotateCcw, Circle, Square } from 'lucide-react-native';
import { hapticsUtil } from '@/lib/haptics';

const log = createLogger('RECORD_VIDEO');

export default function RecordVideoScreen() {
  const router = useRouter();
  const { source, contextId, exerciseKey } = useLocalSearchParams<{
    source?: 'log' | 'feed';
    contextId?: string;
    exerciseKey?: string;
  }>();
  const { theme } = useTheme();

  const [facing, setFacing] = useState<CameraType>('back');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [cameraRef, setCameraRef] = useState<any>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [cameraPermission, requestCameraPermission] = useCameraPermissions();
  const [microphonePermission, requestMicrophonePermission] = useMicrophonePermissions();

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const allPermissionsGranted = cameraPermission?.granted && microphonePermission?.granted;

  const requestAllPermissions = async () => {
    const cameraResult = await requestCameraPermission();
    const micResult = await requestMicrophonePermission();

    if (!cameraResult.granted || !micResult.granted) {
      Alert.alert(
        'Permissions Required',
        'Camera and microphone permissions are required to record videos. Please enable them in your device settings.',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Open Settings', onPress: () => Linking.openSettings() },
        ]
      );
    }
  };

  const flipCamera = () => {
    hapticsUtil.buttonPress();
    setFacing(current => (current === 'back' ? 'front' : 'back'));
  };

  const startRecording = async () => {
    if (!cameraRef || isRecording) return;

    hapticsUtil.buttonPress();
    setIsRecording(true);
    setRecordingTime(0);

    // Start timer
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => prev + 1);
    }, 1000);

    try {
      const video = await cameraRef.recordAsync({
        maxDuration: 60,
      });

      if (video?.uri) {
        // Pass the video URI back with context
        if (source && contextId) {
          if (source === 'log' && exerciseKey) {
            router.replace({
              pathname: '/(tabs)/record',
              params: {
                videoUri: video.uri,
                contextId: contextId,
                exerciseKey: exerciseKey,
                source: 'log'
              },
            } as any);
          } else if (source === 'feed') {
            // V1: Feed is disabled; redirect to record instead
            router.replace('/(tabs)/record');
          } else {
            router.replace('/(tabs)/record');
          }
        } else {
          router.replace('/(tabs)/record');
        }
      }
    } catch (error: any) {
      log.error('Recording error:', error);
      Alert.alert('Oops', "Couldn't record the video. Please try again.");
    } finally {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      setIsRecording(false);
      setRecordingTime(0);
    }
  };

  const stopRecording = () => {
    if (!cameraRef || !isRecording) return;

    hapticsUtil.buttonPress();
    cameraRef.stopRecording();

    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const handleClose = () => {
    if (isRecording) {
      Alert.alert(
        'Stop Recording?',
        'This will discard the current recording.',
        [
          { text: 'Continue Recording', style: 'cancel' },
          {
            text: 'Stop & Close',
            style: 'destructive',
            onPress: () => {
              stopRecording();
              setTimeout(() => router.replace('/(tabs)/record'), 300);
            }
          },
        ]
      );
    } else {
      // Ensure camera is fully released before navigating
      if (isRecording) stopRecording();
      router.replace('/(tabs)/record');
    }
  };

  // Stop recording when screen loses focus or unmounts — releases camera hardware
  useFocusEffect(
    React.useCallback(() => {
      return () => {
        // Screen is blurring (navigating away) — stop recording to release camera
        if (isRecording && cameraRef) {
          try { cameraRef.stopRecording(); } catch {}
        }
      };
    }, [isRecording, cameraRef])
  );

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (Platform.OS === 'web') {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={[styles.webNotice, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.webNoticeText, { color: theme.colors.text }]}>
            Camera recording is not available on web
          </Text>
          <TouchableOpacity
            style={[styles.closeButton, { backgroundColor: theme.colors.primary }]}
            onPress={() => router.replace('/(tabs)/record')}>
            <Text style={[styles.closeButtonText, { color: theme.colors.text }]}>Close</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  if (!cameraPermission || !microphonePermission) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <Text style={[styles.loadingText, { color: theme.colors.text }]}>
          Loading permissions...
        </Text>
      </View>
    );
  }

  if (!allPermissionsGranted) {
    return (
      <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={[styles.permissionContainer, { backgroundColor: theme.colors.surface }]}>
          <Text style={[styles.permissionTitle, { color: theme.colors.text }]}>
            Permissions Required
          </Text>
          <Text style={[styles.permissionText, { color: theme.colors.textSecondary }]}>
            Camera and microphone access are required to record workout videos.
          </Text>
          <TouchableOpacity
            style={[styles.permissionButton, { backgroundColor: theme.colors.primary }]}
            onPress={requestAllPermissions}>
            <Text style={[styles.permissionButtonText, { color: theme.colors.text }]}>Grant Permissions</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.cancelPermissionButton}
            onPress={() => router.replace('/(tabs)/record')}>
            <Text style={[styles.cancelPermissionText, { color: theme.colors.textSecondary }]}>
              Cancel
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <CameraView
        style={styles.camera}
        facing={facing}
        mode="video"
        ref={(ref) => setCameraRef(ref)}
      />

      {/* Recording Indicator */}
      {isRecording && (
        <View style={[styles.recordingIndicator, { backgroundColor: 'rgba(0, 0, 0, 0.7)' }]}>
          <View style={[styles.recordingDot, { backgroundColor: theme.colors.error }]} />
          <Text style={[styles.recordingText, { color: theme.colors.text }]}>REC {formatTime(recordingTime)}</Text>
        </View>
      )}

      {/* Top Controls */}
      <View style={styles.topControls}>
        <TouchableOpacity
          style={[styles.iconButton, { backgroundColor: 'rgba(0, 0, 0, 0.5)' }]}
          onPress={handleClose}
          disabled={isRecording}>
          <X color={theme.colors.text} size={24} />
        </TouchableOpacity>
      </View>

      {/* Bottom Controls */}
      <View style={styles.bottomControls}>
        {/* Flip Camera Button */}
        <TouchableOpacity
          style={[styles.iconButton, { backgroundColor: 'rgba(0, 0, 0, 0.5)' }]}
          onPress={flipCamera}
          disabled={isRecording}>
          <RotateCcw color={theme.colors.text} size={24} />
        </TouchableOpacity>

        {/* Record Button */}
        <TouchableOpacity
          style={[
            styles.recordButton,
            isRecording && styles.recordButtonActive,
          ]}
          onPress={isRecording ? stopRecording : startRecording}
          activeOpacity={0.8}>
          {isRecording ? (
            <Square color={theme.colors.text} size={32} fill={theme.colors.text} />
          ) : (
            <Circle color={theme.colors.text} size={64} strokeWidth={4} />
          )}
        </TouchableOpacity>

        {/* Spacer for symmetry */}
        <View style={{ width: 50 }} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  camera: {
    flex: 1,
  },
  webNotice: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  webNoticeText: {
    fontSize: 18,
    textAlign: 'center',
    marginBottom: 20,
  },
  closeButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 0,
  },
  closeButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  loadingText: {
    fontSize: 16,
    textAlign: 'center',
  },
  permissionContainer: {
    margin: 20,
    padding: 24,
    borderRadius: 2,
    alignItems: 'center',
  },
  permissionTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 12,
    textAlign: 'center',
  },
  permissionText: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 24,
  },
  permissionButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 0,
    width: '100%',
    alignItems: 'center',
  },
  permissionButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  cancelPermissionButton: {
    marginTop: 12,
    padding: 12,
  },
  cancelPermissionText: {
    fontSize: 16,
  },
  recordingIndicator: {
    position: 'absolute',
    top: 60,
    left: 20,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 2,
  },
  recordingDot: {
    width: 12,
    height: 12,
    borderRadius: 4,
    marginRight: 8,
  },
  recordingText: {
    fontSize: 16,
    fontWeight: '700',
  },
  topControls: {
    position: 'absolute',
    top: 60,
    right: 20,
    zIndex: 10,
  },
  bottomControls: {
    position: 'absolute',
    bottom: 40,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    paddingHorizontal: 20,
  },
  iconButton: {
    width: 50,
    height: 50,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  recordButton: {
    width: 80,
    height: 80,
    borderRadius: 4,
    backgroundColor: 'rgba(239, 68, 68, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  recordButtonActive: {
    backgroundColor: 'rgba(239, 68, 68, 1)',
  },
});
